import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Reveal } from './reveal';

type SecaoProps = {
  id: string;
  rotulo: string;
  titulo: ReactNode;
  conduz?: ReactNode;
  children?: ReactNode;
  className?: string;
  /**
   * Adia layout e pintura enquanto a seção está fora da tela.
   *
   * Só para as seções ABAIXO DA DOBRA. Na primeira tela, `content-visibility`
   * atrasaria justamente o que precisa aparecer primeiro, e o LCP pioraria.
   * Quem liga informa também a altura estimada (`alturaEstimada`), senão a
   * seção colapsa para zero enquanto está fora da tela, a barra de rolagem
   * pula e as âncoras do menu passam a apontar para o lugar errado.
   */
  adiarRender?: boolean;
  /** Altura aproximada em px — medida no navegador, não chutada. */
  alturaEstimada?: number;
};

/**
 * Cabeçalho padrão de seção (rótulo + título + subtítulo opcional), com
 * `scroll-mt-24` para a âncora não ficar colada no header sticky. Usado por
 * dores, como-funciona, recursos, segurança, planos e FAQ — o que muda entre
 * elas é só o conteúdo abaixo.
 */
export function Secao({
  id,
  rotulo,
  titulo,
  conduz,
  children,
  className,
  adiarRender = false,
  alturaEstimada = 1200,
}: SecaoProps) {
  return (
    <section
      id={id}
      className={clsx(
        'scroll-mt-24 py-20 sm:py-28',
        adiarRender && 'adia-render',
        className,
      )}
      style={
        adiarRender
          ? { containIntrinsicSize: `auto ${String(alturaEstimada)}px` }
          : undefined
      }
    >
      <div className="mx-auto max-w-6xl px-6">
        {/* O cabeçalho da seção entra com peso FORTE: ele anuncia um assunto
            novo, e entrar igual a um item de lista é o que apagava a hierarquia
            da página inteira. */}
        <Reveal className="flex flex-col gap-4" peso="forte">
          <p className="text-auth-marca-hover text-xs font-medium tracking-[0.14em] uppercase">
            {rotulo}
          </p>
          {/* Mesmo peso e tracking da manchete: os títulos de seção são a
              SEGUNDA voz da página, não uma terceira. Ver o comentário em
              `manchete-animada.tsx` para por que 300 saiu. */}
          <h2 className="text-auth-texto max-w-2xl text-3xl font-medium tracking-[-0.03em] text-balance sm:text-4xl">
            {titulo}
          </h2>
          {conduz ? (
            <p className="text-auth-texto-secundario max-w-xl text-base leading-relaxed">
              {conduz}
            </p>
          ) : null}
        </Reveal>
        {children}
      </div>
    </section>
  );
}
